// import hooks
import { useMemo } from 'react';
import useGetEmployeeWorkAgreements from 'components/employee-portal/leave-requests/hooks/useGetEmployeeWorkAgreements';
import useGetDutyHourTypes from '../hooks/useGetDutyHourTypes';
import useGetCostCenterDuties from '../hooks/useGetCostCenterDuties';

// import types
import type { Option } from 'components/shared/DutyCodeSelector.types';
import type { UseDutyLineOptionsProps } from './useDutyLineOptions.types';
import type { DutyCode } from 'generated/index';

// import utils
import dayjs from 'dayjs';

export const useDutyLineOptions = ({
  fetchKind,
  startDate,
  endDate,
  employeeId,
  date,
  organizationalUnitId
}: UseDutyLineOptionsProps) => {
  // work agreements
  const { data: workAgreements = [], isFetching: waFetching } =
    useGetEmployeeWorkAgreements(
      {
        startDate: dayjs(startDate).toDate(),
        endDate: dayjs(endDate).toDate(),
        employeeId: employeeId ?? 0
      },
      {
        enabled:
          fetchKind === 'workAgreement' &&
          !!employeeId &&
          !!startDate &&
          !!endDate,
        staleTime: 30 * 60 * 1000,
        cacheTime: 2 * 60 * 60 * 1000
      }
    );

  // duty codes (cost center duties)
  const {
    duties,
    showAll,
    setShowAll,
    isFetching: dcFetching
  } = useGetCostCenterDuties({
    date: fetchKind === 'basicDuty' ? date : undefined,
    organizationalUnitId
  });

  // duty-hour types (bespoke hook — returns dccFlags needed for ALLOWED_WITH_NOMINAL filter)
  const { data: dutyHourTypes = [], isFetching: dhtFetching } =
    useGetDutyHourTypes(
      {
        startDate: dayjs(date).startOf('day').toISOString(),
        endDate: dayjs(date).endOf('day').toISOString()
      },
      { enabled: fetchKind === 'dutyHourTypes' && !!date }
    );

  // normalize CostCenterDuty[] to DutyCode-compatible shape
  const adaptedDuties = useMemo(
    () =>
      duties.map((duty) => ({
        dutyCodeId: duty.id,
        dutyCode: duty.dutyCode,
        displayDutyCode: duty.displayDutyCode,
        preferred: duty.preferred,
        periods: duty.periods.map((p) => ({
          dutyCodePeriodId: p.dutyPeriodId,
          startTime: p.startTime,
          endTime: p.endTime,
          dutyHourType: p.dutyHourType
            ? {
                dutyHourTypeId: p.dutyHourType.id,
                code: p.dutyHourType.code,
                description: p.dutyHourType.description,
                colorCode: p.dutyHourType.colorCode
              }
            : undefined
        }))
      })) as unknown as DutyCode[],
    [duties]
  );

  // build <Autocomplete>-ready options
  const options: Option[] = useMemo(() => {
    switch (fetchKind) {
      case 'workAgreement':
        return workAgreements.map((workAgreement) => ({
          value: workAgreement.waId,
          label:
            `${workAgreement.nr} - ${workAgreement.functionDescription} - ` +
            `${workAgreement.brNameShort} - ${workAgreement.contractKind}`,
          id: workAgreement.nr,
          key: workAgreement.waId ?? crypto.randomUUID()
        }));
      case 'basicDuty': {
        const dutyOptions: Option[] = duties.map((duty) => {
          const label =
            duty.dutyCode === duty.displayDutyCode ||
            duty.dutyCode?.startsWith('§')
              ? duty.displayDutyCode
              : `${duty.displayDutyCode} (${duty.dutyCode})`;
          return {
            value: duty.id,
            label,
            id: duty.id,
            key: duty.id ?? crypto.randomUUID()
          };
        });
        if (organizationalUnitId && !showAll) {
          dutyOptions.push({
            value: '__SHOW_MORE__',
            label: '',
            id: '__SHOW_MORE__',
            key: '__SHOW_MORE__'
          });
        }
        return dutyOptions;
      }
      case 'dutyHourTypes':
        return dutyHourTypes
          .filter((type) => type.dccFlags.includes('ALLOWED_WITH_NOMINAL'))
          .map((type) => ({
            value: type.id,
            label: `${type.code} - ${type.description}`,
            id: type.code,
            key: type.id ?? crypto.randomUUID()
          }));
      default:
        return [];
    }
  }, [
    fetchKind,
    workAgreements,
    duties,
    dutyHourTypes,
    organizationalUnitId,
    showAll
  ]);

  return {
    options,
    dutyCodes: adaptedDuties,
    showAll,
    setShowAll,
    isFetching: waFetching || dcFetching || dhtFetching
  };
};
